import React, { useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { useNavigate } from 'react-router-dom'
import ColorPicker from 'layouts/authentication/register/component/color-picker'
import CDButton from 'components/cd-button'
import Loader from 'components/loader'
import { settingsApi } from '../restaurant.api'
import Api from 'utils/api'
import { useAuth } from 'context/auth'

const toInitialValues = (settings) => ({
  style: {
    textColor: settings?.style?.textColor || '#111827',
    headingColor: settings?.style?.headingColor || '#111827',
    searchBarRingColor: settings?.style?.searchBarRingColor || '#d1d5db',
    buttonBackgroundColor: settings?.style?.buttonBackgroundColor || '#FF5E36',
    buttonTextColor: settings?.style?.buttonTextColor || '#ffffff',
    backgroundColor: settings?.style?.backgroundColor || '#ffffff',
    linkColor: settings?.style?.linkColor || '#FF5E36',
  },
})

function Section({ title, description, children }) {
  return (
    <div className="tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-bg-white tw-p-4 tw-flex tw-flex-col tw-gap-4">
      <div>
        <h3 className="tw-font-semibold">{title}</h3>
        {description && <p className="tw-text-xs tw-text-zinc-500 tw-mt-1">{description}</p>}
      </div>
      {children}
    </div>
  )
}

function WebsiteStyle({ onSave }) {
  const navigate = useNavigate()
  const { currentUser, setCurrentUser } = useAuth()
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    ;(async () => {
      const { response } = await settingsApi.get()
      if (response) setSettings(response.data || {})
      setLoading(false)
    })()
  }, [])

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = { style: values.style }
    const { response } = await settingsApi.update(payload)
    setSubmitting(false)
    if (response?.data) {
      setSettings(response.data)
      // Also update account branding so app-wide theme reflects new colors
      try {
        const brandingPayload = {
          pageBackgroundColor: values.style.backgroundColor,
          pageTextColor: values.style.textColor,
          buttonBackgroundColor: values.style.buttonBackgroundColor,
          buttonTextColor: values.style.buttonTextColor,
        }
        const brandingRes = await Api('/account/branding', 'put', { payload: brandingPayload, notify: false })
        if (brandingRes.response?.data?.account) {
          // update auth context so UI uses new branding immediately
          const updatedUser = { ...currentUser }
          updatedUser.account = brandingRes.response.data.account
          setCurrentUser && setCurrentUser({ ...updatedUser })
        }
      } catch (err) {
        // ignore branding update failures
        // eslint-disable-next-line no-console
        console.error('Failed to update account branding', err)
      }

      if (onSave) onSave()
    }
  }

  if (loading) return <Loader />

  const currentStyle = toInitialValues(settings).style
  const colorSummary = [
    { key: 'textColor', label: 'Body text' },
    { key: 'headingColor', label: 'Heading' },
    { key: 'searchBarRingColor', label: 'Search ring' },
    { key: 'linkColor', label: 'Link' },
    { key: 'buttonBackgroundColor', label: 'Button background' },
    { key: 'buttonTextColor', label: 'Button text' },
    { key: 'backgroundColor', label: 'Page background' },
  ]

  return (
    <div>
      <div className="tw-mb-4">
        <button
          onClick={() => navigate('/order-management')}
          className="tw-text-[12px] tw-text-gray-400"
        >
          ← Order management
        </button>
        <h2 className="font-heading tw-text-2xl">Update Your Website</h2>
        <p className="tw-text-sm tw-text-zinc-500 tw-mt-2">
          Change website text, heading, search bar ring and button colors to make your storefront look polished and easy to use.
        </p>
      </div>

      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-3 tw-mb-6">
        {colorSummary.map((item) => (
          <div key={item.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-3xl tw-border tw-border-zinc-200 tw-p-3 tw-bg-white">
            <div
              className="tw-w-10 tw-h-10 tw-rounded-xl tw-border tw-border-zinc-200"
              style={{ backgroundColor: currentStyle[item.key] }}
            />
            <div>
              <p className="tw-text-[11px] tw-uppercase tw-tracking-[0.15em] tw-text-zinc-400">
                {item.label}
              </p>
              <p className="tw-text-sm tw-font-semibold tw-text-zinc-900">{currentStyle[item.key]}</p>
            </div>
          </div>
        ))}
      </div>

      <Formik
        initialValues={toInitialValues(settings)}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={formik.handleSubmit}>
            <Section
              title="Text and heading colors"
              description="Choose colors for body text and headings so your website looks consistent."
            >
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <ColorPicker name="style.textColor" label="Body text color" />
                <ColorPicker name="style.headingColor" label="Heading color" />
              </div>
            </Section>

            <Section
              title="Search and link styles"
              description="Update the search bar ring and link color so users can quickly see interactive elements."
            >
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <ColorPicker name="style.searchBarRingColor" label="Search bar ring color" />
                <ColorPicker name="style.linkColor" label="Link color" />
              </div>
            </Section>

            <Section
              title="Buttons and page background"
              description="Control button colors and the page background to create a coherent website theme."
            >
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <ColorPicker name="style.buttonBackgroundColor" label="Button background color" />
                <ColorPicker name="style.buttonTextColor" label="Button text color" />
              </div>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <ColorPicker name="style.backgroundColor" label="Page background color" />
              </div>
            </Section>

            <div>
              <CDButton
                type="submit"
                processing={formik.isSubmitting}
                label="Save website style"
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  )
}

export default WebsiteStyle
